import { useState } from 'react';

import type {
  So5AppearanceInput,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Sport as SportEnum } from '@sorare/core/src/__generated__/globalTypes';

import { computeBenchObjectsTotalScore } from '#src/hooks/compose/useBenchObjectsTotalScore/index.ts';
import { useCreateOrUpdateLineupMutation } from '#src/hooks/so5/useCreateOrUpdateLineup.ts';

type SwapSlot = {
  lineupId: string;
  slotIndex: number;
};

type LeagueInfo = {
  id?: string;
  slug: string;
  so5League?: { slug: string };
  rules?: {
    appearances: unknown[] | null;
    sumOfAverageScores?: number | null;
  } | null;
  displayedTypedRules?: {
    id?: string;
    __typename?: string;
    max?: number | null;
    immuneAppearances?: number | null;
    appearances?: { id: string }[] | null;
  }[];
};

type LineupInfo = {
  id: string;
  so5Appearances: {
    id?: string;
    index: number;
    captain: boolean;
    boosted?: boolean;
    anyCard?: { slug?: string } | null;
    nbaAverageScore?: number | null;
  }[];
};

type ContenderLike = {
  so5Lineup?: LineupInfo | null;
  so5Leaderboard: LeagueInfo;
};

type LineupSwapMetadata = {
  lineupAverageScores: number[];
  mvpBenchIds: Set<string>;
  remainingPointsForSlotByIndex: Record<number, number | undefined>;
  averageScoresCap?: number;
  sumOfAverageScoresCap?: number;
  immuneAppearances: number;
};

const getOrderedAppearances = (contender: ContenderLike) => {
  const { so5Lineup, so5Leaderboard } = contender;
  if (!so5Lineup) return [];
  const rules = so5Leaderboard.rules?.appearances;
  if (!rules?.length) {
    return [...so5Lineup.so5Appearances].sort((a, b) => a.index - b.index);
  }
  return rules.map((_, index) =>
    so5Lineup.so5Appearances.find(appearance => appearance.index === index)
  );
};

const buildSwapInputs = (
  appearances: ReturnType<typeof getOrderedAppearances>,
  indexToReplace: number,
  newCardSlug: string
): So5AppearanceInput[] | null => {
  const cardSlugs = appearances.map(appearance => appearance?.anyCard?.slug);

  cardSlugs[indexToReplace] = newCardSlug;

  const so5Appearances = appearances.map((appearance, index) => {
    if (!appearance) return null;
    const cardSlug = cardSlugs[index];
    if (!cardSlug) return null;
    const boosted = appearance.boosted ?? false;
    return {
      cardSlug,
      captain: appearance.captain,
      boost: boosted,
    };
  });

  const resolved = so5Appearances.filter(Boolean) as So5AppearanceInput[];

  return resolved;
};

const buildSwapInputsForSingleLineup = (
  appearances: ReturnType<typeof getOrderedAppearances>,
  fromIndex: number,
  toIndex: number
): So5AppearanceInput[] | null => {
  const cardSlugs = appearances.map(appearance => appearance?.anyCard?.slug);
  const fromSlug = cardSlugs[fromIndex];
  const toSlug = cardSlugs[toIndex];
  if (!fromSlug || !toSlug) return null;

  cardSlugs[fromIndex] = toSlug;
  cardSlugs[toIndex] = fromSlug;

  const so5Appearances = appearances.map((appearance, index) => {
    if (!appearance) return null;
    const cardSlug = cardSlugs[index];
    if (!cardSlug) return null;
    const boosted = appearance.boosted ?? false;
    return {
      cardSlug,
      captain: appearance.captain,
      boost: boosted,
    };
  });

  const resolved = so5Appearances.filter(Boolean) as So5AppearanceInput[];

  return resolved;
};

const getBenchObjectsForAverageScore = (
  appearances: ReturnType<typeof getOrderedAppearances>
) => {
  return appearances.map((appearance, index) => ({
    __typename: 'ThresholdPickerTaskAppearance' as const,
    id: appearance?.id || `placeholder-${index}`,
    lastTenPlayedSo5AverageScore: appearance?.nbaAverageScore || 0,
    lastFifteenSo5AverageScore: appearance?.nbaAverageScore || 0,
  }));
};

type ComputeBenchObjectsTotalScoreArgs = Parameters<
  typeof computeBenchObjectsTotalScore
>[0];

const getCombinedRemainingPoints = ({
  averageRuleRemainingPoints,
  sumOfAverageScoresCap,
  lineupAverageScores,
}: {
  averageRuleRemainingPoints: Record<number, number | undefined>;
  sumOfAverageScoresCap?: number;
  lineupAverageScores: number[];
}) => {
  const totalAverageScore = lineupAverageScores.reduce(
    (acc, score) => acc + score,
    0
  );

  return lineupAverageScores.reduce<Record<number, number | undefined>>(
    (acc, score, index) => {
      const averageRuleRemaining = averageRuleRemainingPoints[index];
      const sumRuleRemaining =
        typeof sumOfAverageScoresCap === 'number'
          ? sumOfAverageScoresCap - totalAverageScore + score
          : undefined;

      if (
        typeof averageRuleRemaining === 'number' &&
        typeof sumRuleRemaining === 'number'
      ) {
        acc[index] = Math.min(averageRuleRemaining, sumRuleRemaining);
        return acc;
      }

      if (typeof averageRuleRemaining === 'number') {
        acc[index] = averageRuleRemaining;
        return acc;
      }

      if (typeof sumRuleRemaining === 'number') {
        acc[index] = sumRuleRemaining;
        return acc;
      }

      acc[index] = undefined;
      return acc;
    },
    {}
  );
};

export const getLineupSwapMetadata = ({
  contender,
  sport,
}: {
  contender?: ContenderLike | null;
  sport: Sport;
}): LineupSwapMetadata => {
  if (!contender) {
    return {
      lineupAverageScores: [],
      mvpBenchIds: new Set<string>(),
      remainingPointsForSlotByIndex: {},
      averageScoresCap: undefined,
      sumOfAverageScoresCap: undefined,
      immuneAppearances: 0,
    };
  }

  const appearances = getOrderedAppearances(contender);
  const benchObjectsForAverageScore =
    getBenchObjectsForAverageScore(appearances);
  const displayedTypedRules =
    contender.so5Leaderboard.displayedTypedRules || [];
  const typedDisplayedRules =
    displayedTypedRules as ComputeBenchObjectsTotalScoreArgs['displayedTypedRules'];
  const typedBenchObjects =
    benchObjectsForAverageScore as ComputeBenchObjectsTotalScoreArgs['benchObjects'];
  const averageScoresRule = displayedTypedRules.find(
    rule => rule.__typename === 'So5AverageScoresRule'
  );
  const averageScoresCap = averageScoresRule?.max ?? undefined;
  const sumOfAverageScoresCap =
    contender.so5Leaderboard.rules?.sumOfAverageScores ?? undefined;
  const immuneAppearances = averageScoresRule?.immuneAppearances || 0;
  const lineupAverageScores = benchObjectsForAverageScore.map(
    ({ lastTenPlayedSo5AverageScore }) => lastTenPlayedSo5AverageScore || 0
  );

  const mvpBenchIds =
    sport === SportEnum.NBA
      ? new Set(
          computeBenchObjectsTotalScore({
            displayedTypedRules: typedDisplayedRules,
            benchObjects: typedBenchObjects,
            selectedBenchObject: undefined,
          }).mvpBenchIds
        )
      : new Set<string>();

  const averageRuleRemainingPoints = benchObjectsForAverageScore.reduce<
    Record<number, number | undefined>
  >((acc, _, index) => {
    const { cap, remainingPointsForSlot } = computeBenchObjectsTotalScore({
      displayedTypedRules: typedDisplayedRules,
      benchObjects: typedBenchObjects,
      selectedBenchObject: typedBenchObjects[index],
    });
    acc[index] = cap !== null ? remainingPointsForSlot : undefined;
    return acc;
  }, {});
  const remainingPointsForSlotByIndex = getCombinedRemainingPoints({
    averageRuleRemainingPoints,
    sumOfAverageScoresCap,
    lineupAverageScores,
  });

  return {
    lineupAverageScores,
    mvpBenchIds,
    remainingPointsForSlotByIndex,
    averageScoresCap,
    sumOfAverageScoresCap,
    immuneAppearances,
  };
};

export const useLineupSwap = <T extends ContenderLike>() => {
  const { submit: submitLineupUpdate } = useCreateOrUpdateLineupMutation();
  const [swappingSlots, setSwappingSlots] = useState<string[]>([]);

  const handleSwap = async (
    contenders: T[],
    source: SwapSlot,
    target: SwapSlot
  ) => {
    if (
      source.lineupId === target.lineupId &&
      source.slotIndex === target.slotIndex
    ) {
      return;
    }
    const sourceContender = contenders.find(
      contender => contender.so5Lineup?.id === source.lineupId
    );
    const targetContender = contenders.find(
      contender => contender.so5Lineup?.id === target.lineupId
    );
    if (!sourceContender?.so5Lineup || !targetContender?.so5Lineup) return;
    const isSameLineup = source.lineupId === target.lineupId;

    const sourceLeagueSlug = sourceContender.so5Leaderboard.so5League?.slug;
    const targetLeagueSlug = targetContender.so5Leaderboard.so5League?.slug;
    if (sourceLeagueSlug !== targetLeagueSlug) return;

    const sourceAppearances = getOrderedAppearances(sourceContender);
    const targetAppearances = isSameLineup
      ? sourceAppearances
      : getOrderedAppearances(targetContender);

    const sourceCardSlug = sourceAppearances[source.slotIndex]?.anyCard?.slug;
    const targetCardSlug = targetAppearances[target.slotIndex]?.anyCard?.slug;
    const sourceLeaderboardId = sourceContender.so5Leaderboard.id;
    const targetLeaderboardId = targetContender.so5Leaderboard.id;

    if (
      !sourceCardSlug ||
      !targetCardSlug ||
      !sourceLeaderboardId ||
      !targetLeaderboardId
    ) {
      return;
    }

    const sourceInputs = isSameLineup
      ? buildSwapInputsForSingleLineup(
          sourceAppearances,
          source.slotIndex,
          target.slotIndex
        )
      : buildSwapInputs(sourceAppearances, source.slotIndex, targetCardSlug);
    const targetInputs = isSameLineup
      ? sourceInputs
      : buildSwapInputs(targetAppearances, target.slotIndex, sourceCardSlug);

    if (!sourceInputs || !targetInputs) return;

    const sourceKey = `${source.lineupId}:${source.slotIndex}`;
    const targetKey = `${target.lineupId}:${target.slotIndex}`;

    setSwappingSlots(prev =>
      Array.from(new Set([...prev, sourceKey, targetKey]))
    );

    try {
      if (isSameLineup) {
        await submitLineupUpdate({
          input: {
            so5Appearances: sourceInputs,
            so5LeaderboardId: sourceLeaderboardId,
            so5LineupId: sourceContender.so5Lineup.id,
          },
          so5LeaderboardSlug: sourceContender.so5Leaderboard.slug,
        });
      } else {
        await Promise.all([
          submitLineupUpdate({
            input: {
              so5Appearances: sourceInputs,
              so5LeaderboardId: sourceLeaderboardId,
              so5LineupId: sourceContender.so5Lineup.id,
            },
            so5LeaderboardSlug: sourceContender.so5Leaderboard.slug,
          }),
          submitLineupUpdate({
            input: {
              so5Appearances: targetInputs,
              so5LeaderboardId: targetLeaderboardId,
              so5LineupId: targetContender.so5Lineup.id,
            },
            so5LeaderboardSlug: targetContender.so5Leaderboard.slug,
          }),
        ]);
      }
    } finally {
      setSwappingSlots(prev =>
        prev.filter(key => key !== sourceKey && key !== targetKey)
      );
    }
  };

  return { handleSwap, swappingSlots };
};
